import { useRef, useState } from 'react';
import kit from './kit.module.css';
import styles from './ProgressiveOnboardingDemo.module.css';

type Offer = 'open' | 'deferred' | 'tried' | 'undone';

interface Turn {
  id: number;
  who: 'user' | 'agent';
  text: string;
}

const EXTRA_DECLINES = ['Decline the 4 PM retro on Friday.', 'Decline the 3 PM vendor call on Friday.'];

const CAPABILITIES = [
  { id: 'focus', name: 'Protect focus time', example: 'Block Friday 1-5 PM and decline new invites with a note.' },
  { id: 'slots', name: 'Find a free slot', example: 'Find 30 minutes with Priya next week.' },
  { id: 'buffers', name: 'Add travel buffers', example: 'Add 20 minutes before off-site meetings.' },
];

export function ProgressiveOnboardingDemo() {
  const [offer, setOffer] = useState<Offer>('open');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [declines, setDeclines] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedback, setFeedback] = useState(
    'The agent noticed a pattern and offers one relevant capability, with an example. Try it or defer it.'
  );

  const idRef = useRef(0);
  const nextId = () => (idRef.current += 1);

  const add = (...items: Omit<Turn, 'id'>[]) =>
    setTurns((t) => [...t, ...items.map((i) => ({ ...i, id: nextId() }))]);

  const tryIt = () => {
    setOffer('tried');
    setMenuOpen(false);
    add(
      { who: 'user', text: 'Try it for this Friday.' },
      {
        who: 'agent',
        text: 'Done. Friday 1-5 PM is blocked as focus time. New invites in that window get declined with a short note. This Friday only - nothing else changed.',
      }
    );
    setFeedback('You saw the capability by using it once, on a small scope you can undo.');
  };

  const notNow = () => {
    setOffer('deferred');
    add(
      { who: 'user', text: 'Not now.' },
      { who: 'agent', text: 'No problem. You\'ll find it under "What can you do?" whenever you need it.' }
    );
    setFeedback('Deferred, not lost. Decline another meeting to see whether it pitches again.');
  };

  const undo = () => {
    setOffer('undone');
    add({ who: 'agent', text: 'Removed the focus block. Your Friday is back to how it was.' });
    setFeedback('Undone. The capability stays listed under "What can you do?" if you change your mind.');
  };

  const declineAnother = () => {
    const text = EXTRA_DECLINES[declines];
    setDeclines((d) => d + 1);
    add({ who: 'user', text }, { who: 'agent', text: 'Declined.' });
    setFeedback('Just "Declined." No second pitch - the agent remembers you have already seen focus time.');
  };

  const openFromMenu = (id: string) => {
    if (id !== 'focus') {
      setFeedback('In this demo only focus time is wired up. The others show how examples sit next to each item.');
      return;
    }
    setMenuOpen(false);
    setOffer('open');
    add({ who: 'agent', text: 'Here it is again: "Block Friday 1-5 PM as focus time and decline new invites with a note."' });
    setFeedback('Found it right where the agent said it would be. Ready to try it now.');
  };

  return (
    <div className={kit.root}>
      <div className={kit.stack}>
        <div className={kit.bubbleUser}>Decline the 2 PM sync on Friday.</div>
        <div className={kit.bubbleAgent}>Declined. That's the third Friday meeting you've declined today.</div>

        <div className={kit.proposal}>
          <div className={kit.proposalTop}>
            <span>Something I can do</span>
            <span>{offer === 'tried' ? 'Tried' : offer === 'open' ? 'New to you' : 'Seen'}</span>
          </div>
          <h3 className={kit.proposalTitle}>I can protect your Fridays for you.</h3>
          <p className={styles.example}>
            For example: "Block Friday 1-5 PM as focus time and decline new invites with a note."
          </p>
          {offer === 'open' && turns.length === 0 && (
            <div className={kit.row}>
              <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={tryIt}>
                Try it for this Friday
              </button>
              <button type="button" className={`${kit.btn} ${kit.btnGhost}`} onClick={notNow}>
                Not now
              </button>
            </div>
          )}
        </div>

        {turns.map((t) => (
          <div key={t.id} className={t.who === 'user' ? kit.bubbleUser : kit.bubbleAgent}>
            {t.text}
          </div>
        ))}

        {offer === 'open' && turns.length > 0 && (
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnPrimary}`} onClick={tryIt}>
              Try it for this Friday
            </button>
          </div>
        )}

        {offer === 'tried' && (
          <div className={kit.row}>
            <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={undo}>
              Undo
            </button>
          </div>
        )}
      </div>

      {offer !== 'open' && (
        <>
          <hr className={kit.divider} />
          <div className={kit.row}>
            {offer === 'deferred' && declines < EXTRA_DECLINES.length && (
              <button type="button" className={`${kit.btn} ${kit.btnEdit}`} onClick={declineAnother}>
                {EXTRA_DECLINES[declines]}
              </button>
            )}
            <button
              type="button"
              className={`${kit.btn} ${kit.btnGhost}`}
              aria-expanded={menuOpen}
              onClick={() => {
                setMenuOpen((v) => !v);
                if (!menuOpen) setFeedback('A short list of what it can do, each with an example. Focus time is marked as seen.');
              }}
            >
              What can you do?
            </button>
          </div>
        </>
      )}

      {menuOpen && (
        <ul className={styles.menu}>
          {CAPABILITIES.map((c) => (
            <li key={c.id}>
              <button type="button" className={styles.menuItem} onClick={() => openFromMenu(c.id)}>
                <span className={styles.menuHead}>
                  <span className={styles.menuName}>{c.name}</span>
                  {c.id === 'focus' && <span className={kit.chip}>Seen today</span>}
                </span>
                <span className={styles.menuExample}>{c.example}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className={kit.feedback} aria-live="polite">
        {feedback}
      </p>
    </div>
  );
}
